import { Temporal } from "@js-temporal/polyfill";

/**
 * The one place in `apps/app` that converts between the API's instants and the grid's
 * minutes.
 *
 * CLAUDE.md: never raw `Date` arithmetic for user-facing times, always the Temporal polyfill
 * with explicit IANA zones. Concentrating that here is what makes the rule checkable — every
 * other file in this app takes a number of minutes or a formatted string and cannot get a
 * zone wrong because it never sees one.
 *
 * ── The DST cases this has to survive ─────────────────────────────────────────
 *
 * `Europe/Stockholm` springs forward on the last Sunday of March: 02:00 does not exist. It
 * falls back on the last Sunday of October: 02:30 happens twice. A 09:00 booking made in
 * February must still be 09:00 in April (BUILD.md §6), which is true here because a booking's
 * instant is stored on the server and re-rendered into the zone on every read — the app never
 * adds 24 hours to anything.
 *
 * `dayLengthMinutes` exists for the same reason: 1440 is a guess that is wrong twice a year,
 * and a grid built on it puts every event after 02:00 an hour out of place on those two days.
 */

export { Temporal };

export type IanaZone = string;

/** The API's `2026-08-25T09:00:00+02:00`, in the franchise's zone. */
export function zoned(iso: string, timezone: IanaZone): Temporal.ZonedDateTime {
  return Temporal.Instant.from(iso).toZonedDateTimeISO(timezone);
}

export function today(timezone: IanaZone): Temporal.PlainDate {
  return Temporal.Now.zonedDateTimeISO(timezone).toPlainDate();
}

/**
 * Wall-clock minutes from local midnight.
 *
 * The clock, not elapsed time — see the note atop `packages/ui/src/calendar/geometry.ts` for
 * why the grid wants it that way.
 */
export function minutesOfDay(when: Temporal.ZonedDateTime): number {
  return when.hour * 60 + when.minute;
}

/**
 * How many minutes the given local day actually has: 1440, or 1380, or 1500.
 *
 * `hoursInDay` is exactly the Temporal API that exists for this, and using it means the
 * October Sunday with two 02:30s renders 25 hours of grid rather than silently overlapping
 * an hour of bookings on top of another hour of bookings.
 */
export function dayLengthMinutes(date: Temporal.PlainDate, timezone: IanaZone): number {
  return Math.round(date.toZonedDateTime({ timeZone: timezone }).hoursInDay * 60);
}

/**
 * A start minute on a local date, back to an instant, as the API wants it.
 *
 * `disambiguation: "compatible"` is the ECMAScript default and the right one here: a drag
 * onto a time that does not exist (02:30 on the March Sunday) lands on 03:30 rather than
 * throwing, and a drag onto a time that happens twice takes the first. The server then
 * re-derives everything from the instant it is sent, so whatever the ambiguity resolved to
 * is what the customer's confirmation will say.
 */
export function instantAt(
  date: Temporal.PlainDate,
  minute: number,
  timezone: IanaZone,
): Temporal.ZonedDateTime {
  return date
    .toZonedDateTime({ timeZone: timezone, plainTime: Temporal.PlainTime.from("00:00") })
    .add({ minutes: minute });
}

export function wallClockAt(
  day: string,
  time: string,
  timezone: IanaZone,
): Temporal.ZonedDateTime | null {
  try {
    return Temporal.PlainDate.from(day).toZonedDateTime({
      timeZone: timezone,
      plainTime: Temporal.PlainTime.from(time),
    });
  } catch {
    return null;
  }
}

export const MIN_JOB_MIN = 5;

export function isReadyAfter(
  dropOff: Temporal.ZonedDateTime | null,
  ready: Temporal.ZonedDateTime | null,
): boolean {
  return (
    dropOff !== null &&
    ready !== null &&
    Temporal.ZonedDateTime.compare(ready, dropOff.add({ minutes: MIN_JOB_MIN })) >= 0
  );
}

/** What the API accepts: ISO 8601 with an explicit offset, to the second. */
export function apiInstant(when: Temporal.ZonedDateTime): string {
  return when.toString({ timeZoneName: "never", smallestUnit: "second" });
}

/* ── Formatting ────────────────────────────────────────────────────────────── */

/**
 * `09:00`. 24-hour, always, in every locale this product ships.
 *
 * DESIGN.md §3.4 fixes Swedish conventions — ISO dates, 24-hour times — and `sv-SE` produces
 * them. `en` is a staff-facing locale for a Swedish company rather than a US one, so it gets
 * the same clock: a technician reading "2 PM" off a Swedish workshop's calendar is a
 * misreading waiting to happen.
 */
export function formatTime(when: Temporal.ZonedDateTime): string {
  return `${String(when.hour).padStart(2, "0")}:${String(when.minute).padStart(2, "0")}`;
}

export function formatMinute(minute: number): string {
  const wrapped = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(
    Math.round(wrapped % 60),
  ).padStart(2, "0")}`;
}

export function formatRange(start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime): string {
  // En dash, not a hyphen: it is a range, and Swedish typography is specific about it.
  return `${formatTime(start)}–${formatTime(end)}`;
}

/** `må 24 aug`, the toolbar's date. DESIGN.md §4.1's layout sketch. */
export function formatDayHeading(date: Temporal.PlainDate, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(date.toString()));
}

/** `2026-08-24`. ISO, which is what Swedes expect (DESIGN.md §3.4). */
export function formatIsoDate(date: Temporal.PlainDate): string {
  return date.toString();
}

/**
 * `1 250 kr`. Non-breaking thousands separator, decimals with a comma.
 *
 * Money is integer minor units everywhere in this system (CLAUDE.md) and becomes a string
 * exactly here. `Intl` gets the separator and the symbol right per locale; the division by
 * 100 is the only arithmetic, and it happens once.
 */
export function formatMoney(amountMinor: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "currency",
    currency,
    // Swedish prices are whole kronor in this trade; 1 250,00 kr is noise on a job card.
    minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100);
}

/** ISO 8601 week number, which is what a Swedish workshop means by "vecka 34". */
export function isoWeek(date: Temporal.PlainDate): number {
  return date.weekOfYear ?? 1;
}

/** Monday. `availability_rules.weekday` is 0 = Monday, and so is the week view. */
export function startOfWeek(date: Temporal.PlainDate): Temporal.PlainDate {
  return date.subtract({ days: date.dayOfWeek - 1 });
}

/** `availability_rules.weekday`: 0 = Monday, not JavaScript's 0 = Sunday. */
export function ruleWeekday(date: Temporal.PlainDate): number {
  return date.dayOfWeek - 1;
}

/** `Måndag` for weekday 0. Named by Intl rather than by seven message keys per locale. */
export function weekdayName(weekday: number, locale: string): string {
  // 2024-01-01 was a Monday, which is `availability_rules.weekday` 0.
  const date = new Date(Date.UTC(2024, 0, 1 + weekday));
  const name = new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "long",
    timeZone: "UTC",
  }).format(date);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** `sv` → `sv-SE`, `no` → `nb-NO`. Intl wants a region to pick the right conventions. */
export function intlLocale(locale: string): string {
  if (locale === "sv") return "sv-SE";
  if (locale === "no") return "nb-NO";
  if (locale === "da") return "da-DK";
  if (locale === "fi") return "fi-FI";
  // `en-GB` and not `en-US`: this is a European product and `en` is its third language.
  return "en-GB";
}
